import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './StoreHeader.css';
import { toast } from 'react-toastify';

// آیکون‌ها
import {
    IoChevronDown,
    IoChevronUp,
    IoClose,
    IoStorefrontOutline,
    IoAddCircle,
    IoInformationCircleOutline,
    IoShieldCheckmarkOutline,
    IoMegaphoneOutline
} from "react-icons/io5";

import config from '../../config';
import { useBusinessLogin } from '../../hooks/auth/useBusinessLogin';
import NotificationStore from '../NotificationStore/NotificationStore';

// آدرس تصویر پیش‌فرض فروشگاه
const DEFAULT_STORE_ICON = process.env.PUBLIC_URL + '/images/store_icon.png';

// --- تابع کمکی تصویر ---
const getImageUrl = (imagePath) => {
    if (!imagePath) return DEFAULT_STORE_ICON;
    if (imagePath.startsWith('http')) return imagePath;
    const root = config.api.imageURL.endsWith('/') ? config.api.imageURL.slice(0, -1) : config.api.imageURL;
    const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${root}${path}`;
};

// --- کامپوننت مدیریت لوگو ---
const StoreLogo = ({ src, alt, className, style }) => {
    const initialSrc = getImageUrl(src);
    const [imgSrc, setImgSrc] = useState(initialSrc);

    useEffect(() => {
        setImgSrc(getImageUrl(src));
    }, [src]);

    const handleError = () => {
        if (imgSrc !== DEFAULT_STORE_ICON) {
            setImgSrc(DEFAULT_STORE_ICON);
        }
    };

    const safeStyle = {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'cover',
        borderRadius: '50%',
        ...style
    };

    return (
        <img
            src={imgSrc}
            alt={alt}
            className={className}
            onError={handleError}
            style={safeStyle}
        />
    );
};

// --- کامپوننت اصلی هدر ---
const StoreHeader = ({ activeCollab, defaultBusiness, allCollabs, onSelectCollab, isLoading = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { businessId } = useBusinessLogin();
    const [showNotification, setShowNotification] = useState(false);
    const [accessModalCollab, setAccessModalCollab] = useState(null);

    // ۱. بررسی قطعی بودن وجود دیتای فروشگاه یا همکاری معتبر با چک کردن فیلد id
    const hasBusinessData = Boolean(
        activeCollab?.id ||
        (defaultBusiness && Boolean(defaultBusiness.id))
    );

    // ۲. نمایش اسکلتون فقط زمانی که داده‌ها واقعاً در حال واکشی باشند
    const isFetching = Boolean(isLoading);

    const getCollabImage = (collab) => collab?.business?.image_logo || collab?.business_logo || collab?.image_logo || null;
    const getCollabName = (collab) => collab?.business?.title || collab?.business?.instagram_userName || collab?.business_title || collab?.title || "فروشگاه همکار";

    const currentDisplay = activeCollab
        ? {
            image: getCollabImage(activeCollab),
            name: getCollabName(activeCollab),
            isDefault: false
        }
        : {
            image: defaultBusiness?.image_logo,
            name: defaultBusiness?.title || "فروشگاه بدون نام",
            isDefault: true
        };

    useEffect(() => {
        const closeMenu = (e) => {
            if (!e.target.closest('.sh-store-selector-wrapper')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, []);

    const toggleMenu = () => {
        if (allCollabs && allCollabs.length > 0) {
            setIsOpen(!isOpen);
        }
    };

    const handleCreateStoreClick = () => {
        toast.info("برای شروع و ساخت فروشگاه از قسمت خرید و تمدید اشتراک اقدام فرمائید.", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
            rtl: true,
            style: { zIndex: 99999999 }
        });
    };

    const handleStoreChange = (collab, isDefault) => {
        const newStoreName = isDefault
            ? (defaultBusiness?.title || "فروشگاه بدون نام")
            : getCollabName(collab);

        toast.success(`وضعیت پنل به فروشگاه «${newStoreName}» تغییر یافت.`, {
            toastId: "store-change",
            position: "top-center",
            autoClose: 1500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            rtl: true,
            style: { zIndex: 99999999 }
        });

        onSelectCollab(collab);
        setIsOpen(false);
    };

    const openAccessModal = (e, collab) => {
        e.stopPropagation();
        setAccessModalCollab(collab);
        setIsOpen(false);
    };

    return (
        <div className="sh-store-header-container">
            {showNotification && (
                <NotificationStore
                    isShow={showNotification}
                    fn_close={() => setShowNotification(false)}
                    businessId={businessId}
                />
            )}

            {accessModalCollab && createPortal(
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 999999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    backdropFilter: 'blur(3px)'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '400px',
                        padding: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                        direction: 'rtl', display: 'flex', flexDirection: 'column'
                    }}>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            borderBottom: '1px solid #f0f0f0', paddingBottom: '15px', marginBottom: '20px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2e7d32' }}>
                                <IoShieldCheckmarkOutline size={22} />
                                <h3 style={{ margin: 0, fontSize: '15px', color: '#333', fontWeight: 'bold' }}>سطح دسترسی شما</h3>
                            </div>
                            <button
                                onClick={() => setAccessModalCollab(null)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: '5px', display: 'flex', alignItems: 'center', color: '#888'
                                }}
                            >
                                <IoClose size={24} />
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <StoreLogo
                                src={getCollabImage(accessModalCollab)}
                                alt="logo"
                                style={{
                                    width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover',
                                    border: '1px solid #ddd', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    backgroundColor: '#f9f9f9', marginBottom: '15px'
                                }}
                            />

                            <p style={{ fontSize: '13px', color: '#555', textAlign: 'center', marginBottom: '20px', lineHeight: '1.6' }}>
                                شما به عنوان همکار در فروشگاه <strong>{accessModalCollab.name}</strong> نقش‌های زیر را برعهده دارید:
                            </p>

                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0', width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {(() => {
                                    let activePermissions = [];
                                    try {
                                        if (accessModalCollab.permission) {
                                            const parsedPerms = typeof accessModalCollab.permission === 'string'
                                                ? JSON.parse(accessModalCollab.permission)
                                                : accessModalCollab.permission;
                                            activePermissions = parsedPerms.filter(p => p.checked === true || p.checked === "true");
                                        }
                                    } catch (e) { }

                                    if (activePermissions.length > 0) {
                                        return activePermissions.map((perm, idx) => (
                                            <li key={idx} style={{
                                                background: '#f8fafd', padding: '12px 15px', borderRadius: '8px',
                                                fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px',
                                                color: '#333', border: '1px solid #eef2f6'
                                            }}>
                                                <div style={{ width: '8px', height: '8px', background: '#1B98E3', borderRadius: '50%' }}></div>
                                                {perm.title}
                                            </li>
                                        ));
                                    } else {
                                        return (
                                            <li style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '10px' }}>
                                                دسترسی خاصی یافت نشد.
                                            </li>
                                        );
                                    }
                                })()}
                            </ul>
                        </div>

                        <button
                            onClick={() => setAccessModalCollab(null)}
                            style={{
                                width: '100%', padding: '12px', background: '#1B98E3', color: '#fff',
                                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            متوجه شدم
                        </button>
                    </div>
                </div>,
                document.body
            )}

            {/* ۱. نمایش وضعیت اسکلتون فقط هنگام واکشی اطلاعات از سرور */}
            {isFetching ? (
                <div className="sh-store-pill" style={{ cursor: 'default', pointerEvents: 'none' }}>
                    <div className="sh-store-info" style={{ alignItems: 'center' }}>
                        <div
                            className="sh-skeleton-box"
                            style={{ width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0 }}
                        />
                        <div className="sh-store-text" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div className="sh-skeleton-box sh-skeleton-line-title" style={{ width: '85px', height: '14px' }} />
                            <div className="sh-skeleton-box sh-skeleton-line-sub" style={{ width: '45px', height: '10px' }} />
                        </div>
                    </div>
                </div>
            ) : hasBusinessData ? (
                /* ۲. در صورت وجود فروشگاه یا انتخاب همکاری */
                <div className={`sh-store-selector-wrapper ${isOpen ? 'open' : ''}`}>
                    <div className="sh-store-pill" onClick={toggleMenu}>
                        <div className="sh-store-info">
                            <StoreLogo
                                src={currentDisplay.image}
                                alt="Store Logo"
                                className="sh-store-logo-circle"
                            />
                            <div className="sh-store-text">
                                <span className="sh-store-name">{currentDisplay.name}</span>
                            </div>
                        </div>

                        {allCollabs && allCollabs.length > 0 && (
                            <span className="sh-chevron-icon">{isOpen ? <IoChevronUp /> : <IoChevronDown />}</span>
                        )}
                    </div>

                    {isOpen && (
                        <div className="sh-store-dropdown">
                            {!currentDisplay.isDefault && (
                                <div className="sh-dropdown-item" onClick={() => handleStoreChange(null, true)}>
                                    <StoreLogo src={defaultBusiness?.image_logo} className="sh-dropdown-logo" />
                                    <span>{defaultBusiness?.title || "فروشگاه بدون نام"}</span>
                                </div>
                            )}
                            {allCollabs.map((collab) => (
                                <div
                                    key={collab.id}
                                    className={`sh-dropdown-item ${activeCollab?.id === collab.id ? 'active' : ''}`}
                                    onClick={() => handleStoreChange(collab, false)}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <StoreLogo src={getCollabImage(collab)} className="sh-dropdown-logo" />
                                        <span>{getCollabName(collab)}</span>
                                    </div>

                                    <button
                                        className="sh-access-info-btn"
                                        onClick={(e) => openAccessModal(e, collab)}
                                        title="مشاهده سطح دسترسی من"
                                        style={{
                                            background: 'none', border: 'none', color: '#6b7280',
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px'
                                        }}
                                    >
                                        <IoInformationCircleOutline size={20} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                /* ۳. لودینگ تمام شده و هیچ فروشگاهی وجود ندارد */
                <div
                    className="sh-no-store-wrapper"
                    onClick={handleCreateStoreClick}
                >
                    <div className="sh-no-store-icon-bg">
                        <IoStorefrontOutline />
                    </div>
                    <div className="sh-no-store-text-col">
                        <span className="sh-no-store-title">فروشگاه خود را بسازید</span>
                        <span className="sh-no-store-subtitle">برای شروع کلیک کنید</span>
                    </div>
                    <IoAddCircle className="sh-add-plus-icon" />
                </div>
            )}

            <div className="sh-header-actions">
                <button
                    className="sh-action-btn"
                    onClick={() => setShowNotification(true)}
                    title="پیام‌ها و اعلانات"
                >
                    <IoMegaphoneOutline size={24} />
                </button>
            </div>
        </div>
    );
};

export default StoreHeader;
